import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { ClipLoader } from "react-spinners"
import { toast } from "sonner"

import { sendOtp, verifyOtp } from "@/actions/auth-actions"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatPhoneNumber } from "@/lib/utils"

export const Route = createFileRoute("/login")({
  component: LoginPage,
})

function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otp, setOtp] = useState("")
  const [shouldShowOtpInput, setShowOtpInput] = useState(false)
  const router = useRouter()

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = e.target.value.replace(/\D/g, "")
    if (cleanedValue.length <= 10) {
      setPhoneNumber(cleanedValue)
    }
  }

  async function signInWithPhoneNumber() {
    try {
      setIsLoading(true)
      if (shouldShowOtpInput) {
        const result = await verifyOtp({
          data: { phone: phoneNumber, token: otp },
        })
        if (result.hasError) throw new Error(result.message)
        await router.invalidate()
        router.navigate({ to: result.redirectTo })
      } else {
        const result = await sendOtp({ data: { phone: phoneNumber } })
        if (result.hasError) throw new Error(result.message)
        setShowOtpInput(true)
        toast.success("Verification code sent!")
      }
    } catch (error: unknown) {
      console.error("Error with phone authentication:", error)
      toast.error(
        error instanceof Error ? error.message : "Failed to authenticate"
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle>WattBot Login</CardTitle>
          <CardDescription>
            Enter your phone number to get a login code.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            placeholder="(123) 456-7890"
            type="tel"
            value={formatPhoneNumber(phoneNumber)}
            onChange={handlePhoneChange}
            disabled={isLoading || shouldShowOtpInput}
          />
          {shouldShowOtpInput && (
            <div className="mt-4 grid gap-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                placeholder="Enter verification code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={isLoading}
              />
            </div>
          )}
          <Button
            onClick={signInWithPhoneNumber}
            disabled={isLoading || !phoneNumber || (shouldShowOtpInput && !otp)}
            className="mt-6 w-full"
          >
            {isLoading ? <ClipLoader color="#000" size={16} /> : null}
            {shouldShowOtpInput ? "Verify Code" : "Send Code"}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
